import { useLazyQuery, useMutation } from "@apollo/client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CreateCustomerInput, Customer } from "@/gql/graphql";
import { UPDATE_APPOINTMENT_MUTATION } from "@/graphql/appointments";
import { CHECK_CUSTOMER_EMAIL_EXISTS_QUERY, CREATE_CUSTOMER_MUTATION } from "@/graphql/customers";

interface ReassignCustomerParams {
  salonId: string;
  appointmentId: string;
  selectedCustomer: Customer | null;
  customerInput: string;
  customerEmail: string;
  customerPhone: string;
}

export function useReassignCustomer() {
  const { t } = useTranslation("appointments");
  const [createCustomer] = useMutation(CREATE_CUSTOMER_MUTATION);
  const [updateAppointment] = useMutation(UPDATE_APPOINTMENT_MUTATION);
  const [checkEmailExists] = useLazyQuery(CHECK_CUSTOMER_EMAIL_EXISTS_QUERY);
  const [isReassigning, setIsReassigning] = useState(false);

  const reassignCustomer = async (params: ReassignCustomerParams): Promise<boolean> => {
    const {
      salonId,
      appointmentId,
      selectedCustomer,
      customerInput,
      customerEmail,
      customerPhone,
    } = params;

    setIsReassigning(true);
    try {
      let finalCustomerId: string;
      let finalBookingPhoneId: string | undefined;

      if (selectedCustomer) {
        // Existing customer selected
        finalCustomerId = selectedCustomer.id;
        // Use their primary phone for booking
        finalBookingPhoneId =
          selectedCustomer.primaryPhone?.id || selectedCustomer.phoneNumbers?.[0]?.id;
      } else {
        // Create new customer

        // Check if email exists (if provided)
        if (customerEmail.trim()) {
          const { data: emailCheckData } = await checkEmailExists({
            variables: {
              salonId,
              email: customerEmail.trim(),
            },
          });

          if (emailCheckData?.checkCustomerEmailExists) {
            toast.error(t("booking.customerStep.errors.emailExists"));
            setIsReassigning(false);
            return false;
          }
        }

        // Parse name
        const names = customerInput.trim().split(" ");
        const newFirstName = names[0];
        const newLastName = names.length > 1 ? names.slice(1).join(" ") : "";

        // Create customer input
        const newCustomerInput: CreateCustomerInput = {
          firstName: newFirstName,
          lastName: newLastName,
          email: customerEmail.trim() || undefined,
          phones: customerPhone.trim()
            ? [
                {
                  phone: customerPhone.trim(),
                  label: "mobile",
                  isPrimary: true,
                },
              ]
            : undefined,
        };

        // Create the customer
        const { data: createData } = await createCustomer({
          variables: {
            salonId,
            input: newCustomerInput,
          },
        });

        if (!createData?.createCustomer?.id) {
          throw new Error("Failed to create customer");
        }

        finalCustomerId = createData.createCustomer.id;
        // Get the booking phone ID if phone was provided
        if (customerPhone.trim() && createData.createCustomer.phoneNumbers?.[0]) {
          finalBookingPhoneId = createData.createCustomer.phoneNumbers[0].id;
        }
      }

      // Update appointment with new customer and booking phone
      await updateAppointment({
        variables: {
          salonId,
          appointmentId,
          input: {
            customerId: finalCustomerId,
            bookingPhoneId: finalBookingPhoneId || null,
          },
        },
        refetchQueries: ["Appointment"],
        awaitRefetchQueries: true,
      });

      toast.success(
        selectedCustomer
          ? t("messages.appointmentReassigned", {
              firstName: selectedCustomer.firstName,
              lastName: selectedCustomer.lastName,
            })
          : t("messages.customerCreatedAndAssigned")
      );

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to reassign appointment";
      toast.error(errorMessage);
      return false;
    } finally {
      setIsReassigning(false);
    }
  };

  return {
    reassignCustomer,
    isReassigning,
  };
}
